import React, { useEffect, useMemo, useState } from 'react';

import { useUserPermission } from '@contexts/UserContext';

import FlexContainer from '@components/Gui/FlexContainer';
import Money from '@components/Gui/Money';
import { NwAddButton } from '@components/Gui/NwButton';
import { NwCardTabs } from '@components/Gui/NwCard/NwCardTabs';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';

import AddTransactionDrawer from './AddTransactionDrawer';
import InvoicesWrapper from './Invoices/InvoicesWrapper';
import PaymentsWrapper from './Payments/PaymentsWrapper';
import { useStatementContext, useStatementDispatch } from './StatementContext';
import StatementExporter from './StatementExporter/StatementExporter';
import { TabContent } from './StatementUI';
import StatementWrapper from './StatementWrapper';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CloseIconStyle = {
    marginLeft: '1rem',
};

/**
 * StatementTabs
 *
 * Manages the tab navigation for statements.
 * Handles different tab configurations based on contact type:
 * - client: only Account Receivable
 * - model/agency/service: Account Payable + Account Receivable + Payments + Invoices
 */
const StatementTabs = ({ statement, payments, onClose, isLoading, statBalances }) => {
    const { statementType, contactType, contactID } = useStatementContext();
    const dispatch = useStatementDispatch();

    const AccessAccounting = useUserPermission('AccessAccounting');
    const CreateStatementRowOnModel = useUserPermission('CreateStatementRowOnModel');
    const CreateStatementRowOnClient = useUserPermission('CreateStatementRowOnClient');
    const CreateStatementRowOnAgency = useUserPermission('CreateStatementRowOnAgency');
    const CreateStatementRowOnService = useUserPermission('CreateStatementRowOnService');

    //if statBalances has at least one value in agency or client, return '3rem', otherwise return null
    const tabsMinHeight = useMemo(() => {
        if (
            (statBalances?.agencyTotalBalances && Object.keys(statBalances?.agencyTotalBalances).length > 0) ||
            (statBalances?.clientTotalBalances && Object.keys(statBalances?.clientTotalBalances).length > 0)
        ) {
            return '3rem';
        } else {
            return null;
        }
    }, [statBalances]);

    const onReload = () => {
        dispatch({ type: 'StartReload' });
    };

    const onChangeStatementType = (type) => {
        dispatch({ type: 'ChangeStatementType', value: type });
    };

    // Determine if contact can have payable statement (supplier statement)
    const hasPayableStatement = useMemo(() => {
        // Model, agency and service contacts can have payable statements
        return contactType === 'model' || contactType === 'agency' || contactType === 'service';
    }, [contactType]);

    // Determine if contact can have receivable statement (client statement)
    const hasReceivableStatement = useMemo(() => {
        // Only agency and client contacts can have receivable statements
        return contactType === 'agency' || contactType === 'client';
    }, [contactType]);

    // Determine if we need payments tab (all except client)
    const needPayments = useMemo(() => {
        return contactType !== 'client';
    }, [contactType]);

    // Determine if we need invoices tab (based on contactType, not statementType)
    const needInvoices = useMemo(() => {
        return contactType !== 'service';
    }, [contactType]);

    // Build tabs array
    const statementTabs = useMemo(() => {
        const tabs = [];

        // Determine if contact has both statement types (only agency)
        const hasBothStatements = hasPayableStatement && hasReceivableStatement;

        // Account Payable tab (for suppliers: model, agency, service)
        if (hasPayableStatement) {
            let tabLabelPayable = hasBothStatements ? 'Account Payable' : 'Statement';
            let balancePayable = null;
            if (statBalances) {
                const balances = statBalances.data?.agencyTotalBalances || statBalances.agencyTotalBalances;
                if (balances && Object.keys(balances).length > 0) {
                    const balanceComponents = Object.entries(balances).map(([currency, amount], index) => (
                        <React.Fragment key={currency}>
                            {index > 0 && ', '}
                            <Money amount={amount} currency={currency} />
                        </React.Fragment>
                    ));
                    balancePayable = <span>({balanceComponents})</span>;
                }
            }
            tabs.push({
                key: 'statement-payable',
                icon: byPrefixAndName.fasl['file-invoice-dollar'],
                name: tabLabelPayable,
                details: balancePayable,
            });
        }

        // Account Receivable tab (for clients)
        if (hasReceivableStatement) {
            let tabLabelReceivable = hasBothStatements ? 'Account Receivable' : 'Statement';
            let balanceReceivable = null;
            if (statBalances) {
                const balances = statBalances.data?.clientTotalBalances || statBalances.clientTotalBalances;
                if (balances && Object.keys(balances).length > 0) {
                    const balanceComponents = Object.entries(balances).map(([currency, amount], index) => (
                        <React.Fragment key={currency}>
                            {index > 0 && ', '}
                            <Money amount={amount} currency={currency} />
                        </React.Fragment>
                    ));
                    balanceReceivable = <span>({balanceComponents})</span>;
                }
            }
            tabs.push({
                key: 'statement-receivable',
                icon: byPrefixAndName.fasl['receipt'],
                name: tabLabelReceivable,
                details: balanceReceivable,
            });
        }

        // Payments tab
        if (needPayments) {
            tabs.push({
                key: 'payments',
                icon: byPrefixAndName.fasl['money-check-dollar-pen'],
                name: 'Payments',
            });
        }

        // Invoices tab
        if (needInvoices && AccessAccounting) {
            tabs.push({
                key: 'invoices',
                icon: byPrefixAndName.fasl['file-invoice'],
                name: 'Invoices',
            });
        }

        return tabs;
    }, [hasPayableStatement, hasReceivableStatement, needPayments, needInvoices, AccessAccounting, statBalances]);

    // Determine starting tab
    const startingTab = useMemo(() => {
        // Client starts with receivable
        if (contactType === 'client') {
            return 'statement-receivable';
        }
        // Model, agency and service start with payable
        return 'statement-payable';
    }, [contactType]);

    const [activeTab, setActiveTab] = useState(startingTab);
    const [showDrawer, setShowDrawer] = useState(false);
    const [showDownloadDrawer, setShowDownloadDrawer] = useState(false);

    // Sync activeTab with statementType changes from context
    useEffect(() => {
        if (statementType === 'client') {
            setActiveTab('statement-receivable');
        } else if (statementType === contactType) {
            setActiveTab('statement-payable');
        }
    }, [statementType, contactType]);

    // Handle tab change
    const handleTabChange = (tab) => {
        if (tab === 'statement-payable') {
            onChangeStatementType(contactType); // Use contactType as statementType for payable
        } else if (tab === 'statement-receivable') {
            onChangeStatementType('client');
        }
        setActiveTab(tab);
    };

    // Determine if user can add rows based on contact type
    const canAddRows = useMemo(() => {
        switch (contactType.toLowerCase()) {
            case 'model':
                return CreateStatementRowOnModel;
            case 'client':
                return CreateStatementRowOnClient;
            case 'agency':
                return CreateStatementRowOnAgency;
            case 'service':
                return CreateStatementRowOnService;
            default:
                return false;
        }
    }, [
        contactType,
        CreateStatementRowOnModel,
        CreateStatementRowOnClient,
        CreateStatementRowOnAgency,
        CreateStatementRowOnService,
    ]);

    const handleCreateNewTransaction = () => {
        setShowDrawer(true);
    };

    // Render tab content
    const renderTabContent = () => {
        switch (activeTab) {
            case 'statement-payable':
            case 'statement-receivable':
                return (
                    <StatementWrapper
                        statement={statement}
                        payments={payments}
                        isPayable={activeTab === 'statement-payable'}
                        isLoading={isLoading}
                    />
                );
            case 'payments':
                return <PaymentsWrapper contactID={contactID} onUpdateTransactions={onReload} />;
            case 'invoices':
                return <InvoicesWrapper contactID={contactID} onUpdateTransactions={onReload} />;
            default:
                return <div />;
        }
    };

    // Get extra content for tab bar
    const getExtraContent = () => {
        if (activeTab.includes('statement')) {
            return (
                <NwAddButton
                    disabled={!canAddRows}
                    ghost
                    label={'New transaction'}
                    onClick={handleCreateNewTransaction}
                />
            );
        }
        return null;
    };

    return (
        <>
            <NwCardTabs
                tabsMinHeight={tabsMinHeight}
                tabs={statementTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                tabBarExtraContent={
                    <FlexContainer>
                        {getExtraContent()}
                        {onClose && (
                            <NwIcon
                                pointer
                                icon={byPrefixAndName.fasl['times']}
                                onClick={onClose}
                                style={CloseIconStyle}
                            />
                        )}
                    </FlexContainer>
                }
            >
                <TabContent>{renderTabContent()}</TabContent>
            </NwCardTabs>

            {showDownloadDrawer && (
                <NwDrawer title={'Export Statement'} onClose={() => setShowDownloadDrawer(false)}>
                    <StatementExporter data={statement} />
                </NwDrawer>
            )}

            {showDrawer && (
                <AddTransactionDrawer
                    onClose={() => setShowDrawer(false)}
                    contactType={contactType}
                    contactID={contactID}
                />
            )}
        </>
    );
};

export default StatementTabs;
